import React, { useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Loader2, Copy, CheckCircle, FileText, Star } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIQuizGeneratorPage = () => {
  const [topic, setTopic] = useState('');
  const [numQuestions, setNumQuestions] = useState(5);
  const [difficulty, setDifficulty] = useState('moyen');
  const [quizType, setQuizType] = useState('knowledge');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!topic.trim()) { toast.error('Veuillez saisir un sujet'); return; }
    setGenerating(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/ai/generate-quiz`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic, num_questions: numQuestions, difficulty, type: quizType }),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        if (data.questions?.length > 0) toast.success(`${data.questions.length} questions générées !`);
        else toast.error('Aucune question générée');
      } else {
        toast.error('Erreur lors de la génération');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(result.questions, null, 2));
    toast.success('Questions copiées dans le presse-papier');
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="ai-quiz-generator-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-teal-500" />
            Générateur de quiz IA
          </h1>
          <p className="text-slate-500">Générez automatiquement des questionnaires grâce à l'intelligence artificielle</p>
        </div>

        {/* Config */}
        <Card>
          <CardHeader><CardTitle>Configuration du quiz</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Sujet / Thématique *</label>
              <Input
                placeholder="Ex: Leadership et Management Éducatif, Pédagogie différenciée..."
                value={topic}
                onChange={e => setTopic(e.target.value)}
                data-testid="input-topic"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Type</label>
                <select className="w-full border rounded-md px-3 py-2 text-sm" value={quizType} onChange={e => setQuizType(e.target.value)} data-testid="select-type">
                  <option value="knowledge">Évaluation des acquis (QCM)</option>
                  <option value="satisfaction">Questionnaire de satisfaction</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Nombre de questions</label>
                <Input type="number" min={1} max={20} value={numQuestions} onChange={e => setNumQuestions(parseInt(e.target.value) || 5)} data-testid="input-num-questions" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Difficulté</label>
                <select className="w-full border rounded-md px-3 py-2 text-sm" value={difficulty} onChange={e => setDifficulty(e.target.value)} data-testid="select-difficulty">
                  <option value="facile">Facile</option>
                  <option value="moyen">Moyen</option>
                  <option value="difficile">Difficile</option>
                </select>
              </div>
            </div>
            <Button
              className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white w-full py-5"
              onClick={handleGenerate}
              disabled={generating || !topic.trim()}
              data-testid="generate-btn"
            >
              {generating ? (
                <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Génération en cours...</>
              ) : (
                <><Sparkles className="h-5 w-5 mr-2" />Générer le quiz</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        {result && result.questions?.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                  {result.questions.length} questions générées
                </CardTitle>
                <Button variant="outline" size="sm" onClick={copyToClipboard} data-testid="copy-btn">
                  <Copy className="h-4 w-4 mr-2" />Copier JSON
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {result.questions.map((q, i) => (
                  <div key={q.id || i} className="p-4 rounded-lg border bg-slate-50" data-testid={`generated-question-${i}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-teal-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={q.type === 'choice' ? 'bg-blue-100 text-blue-700' : q.type === 'rating' ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-700'}>
                            {q.type === 'choice' ? 'QCM' : q.type === 'rating' ? 'Notation' : 'Texte libre'}
                          </Badge>
                          {q.required && <Badge variant="outline" className="text-xs">Obligatoire</Badge>}
                        </div>
                        <p className="font-medium text-slate-900 mb-2">{q.question}</p>
                        {q.options && (
                          <div className="grid grid-cols-2 gap-2">
                            {q.options.map((opt, j) => (
                              <div key={j} className={`px-3 py-2 rounded border text-sm ${opt === q.correct ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-medium' : 'bg-white border-slate-200'}`}>
                                {opt === q.correct && <CheckCircle className="h-4 w-4 inline mr-1" />}
                                {opt}
                              </div>
                            ))}
                          </div>
                        )}
                        {q.type === 'rating' && (
                          <div className="flex gap-1 mt-1">
                            {[1,2,3,4,5].map(n => <Star key={n} className="h-5 w-5 text-teal-300" />)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {result && result.error && (
          <Card className="border-red-200">
            <CardContent className="pt-6">
              <p className="text-red-600">Erreur de parsing. Réponse brute de l'IA :</p>
              <pre className="mt-2 p-3 bg-slate-50 rounded text-xs overflow-auto">{result.raw_response}</pre>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default AIQuizGeneratorPage;
